
import type { DailyKeywordData } from '@empathybroker/tsclient-stats/dist/models/daily-keywords/daily-keyword-data'
import type {
  Params,
  SortedStreamGraphConfig,
  SortedStreamGraphData,
  SortedStreamGraphDataItemValue,
} from '@empathyco/insights'
import type { PropType } from 'vue'
import type {
  CategoriesTreeItem,
  Dictionary,
  KeywordData,
  KeywordsOverallData,
  RegularFilterSelectedValues,
  WidgetOptions,
  WidgetOptionsItem,
} from '../../models'
import type { NoDataMessage } from './no-data.component.vue'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { KeywordsTrendsModeEnum } from '@empathybroker/tsclient-stats/dist/enums/keywords-trends-mode.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { SortedStreamGraphComponent } from '@empathyco/insights'

import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { FeatureIdEnum, KeywordsCategoryModeEnum, KeywordsModeEnum, KpiEnum } from '../../enums'

import { InsightsMixin } from '../../mixins'

import { APP_CONSTS } from '../../store/app/app.consts'

import { FeaturesTreeUtils } from '../../utils'

import Widget from '../widget.component.vue'
import NoData from './no-data.component.vue'

export interface KeywordsDataTop {
  top: Dictionary<KeywordData[]>
}

export default defineComponent({
  name: 'KeywordsWidget',
  components: {
    Widget,
    NoData,
  },
  mixins: [InsightsMixin],
  props: {
    setup: Object as PropType<{ options: WidgetOptions }>,
    period: {
      type: Object as PropType<{ startDate: Date; endDate: Date }>,
      required: true,
    },
    filters: Object as PropType<Record<string, RegularFilterSelectedValues>>,
    category: Object as PropType<CategoriesTreeItem>,
    noData: Object as PropType<Dictionary<string>>,
  },
  data(): Record<string, any> {
    const messages = this.$epb.messages.highlights.widget.keywords
    return {
      baseClass: 'epb-keywords-widget',
      title: messages.title,
      loadingData: true,
      isInit: false,
      maxKeywordsTop: 3,
      component: undefined,
      messages,
    }
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    subtitle(): string {
      if (this.category) {
        return ''
      }
      return this.$epb.utils.compileMessage(this.messages.subtitle, {
        count: this.maxKeywordsTop,
        ...(!this.isCategoryMode && {
          mode: this.messages.modes[this.mode],
        }),
      })
    },
    isCategoryMode(): boolean {
      return this.mode === KeywordsCategoryModeEnum.CATEGORY
    },
    feature(): FeatureIdEnum {
      const validCategoryFeature = FeaturesTreeUtils.getFeatureNode(
        FeatureIdEnum.KEYWORDS,
        this.featuresTree[DomainEnum.SEARCH],
      )

      return validCategoryFeature?.routeName as FeatureIdEnum
    },
    options(): WidgetOptions {
      if (this.category) {
        return {
          items: [],
          documentationLink: this.messages.link,
        }
      }
      return this.setup?.options || this.defaultOptions
    },
    defaultOptions(): WidgetOptions {
      return {
        items: Object.values(KeywordsModeEnum).map(
          (keywordsMode: KeywordsModeEnum): WidgetOptionsItem => ({
            id: keywordsMode,
            label: this.messages.modes[keywordsMode],
            selected: keywordsMode === KeywordsModeEnum.GROWTH,
          }),
        ),
        documentationLink: this.messages.link,
      }
    },
    hasDataLoaded(): boolean {
      return !!this.data?.top[this.mode]?.length
    },
    noDataMessage(): NoDataMessage {
      return {
        title: this.noData.title,
        ...(this.filters && { subtitle: this.noData.filters }),
        ...(this.category && { subtitle: this.noData.category }),
      }
    },
    mode(): KeywordsCategoryModeEnum {
      if (this.category) {
        return KeywordsCategoryModeEnum.CATEGORY
      }
      return this.options.items.find((item: WidgetOptionsItem) => item.selected)?.id
    },
    componentParams(): Params<SortedStreamGraphConfig, SortedStreamGraphData> {
      return {
        config: {
          locale: this.$epb.locale,
          itemNameWidth: 105,
          deselectIconSize: 0,
          valueNameHeight: 34,
          valueNameWidth: 30,
          columnWidth: 8,
          valueSuffix:
            this.$epb.messages.insights.analytics.kpis[KpiEnum.QUERIES].valueLabel.toUpperCase(),
          areaOpacity: 0.5,
          showDefaultCursor: true,
        },
        data: this.componentData,
      }
    },
    componentData(): SortedStreamGraphData {
      const keywordsData = this.data?.top[this.mode] || []
      return keywordsData.map((keywordData: KeywordData) => ({
        name: keywordData.overall.keyword.toUpperCase(),
        values: this.transformComponentDataItemValues(keywordData),
      }))
    },
    categoryParams(): { categoryId: string; level: number; parentId?: string } {
      return {
        categoryId: this.category.id,
        level: this.category.level,
        ...(this.category.level > 1 && { parentId: this.category.parentId }),
      }
    },
  },
  watch: {
    mode: {
      async handler(): Promise<void> {
        await this.loadData(false, false)
      },
    },
    componentParams: {
      handler(
        newParams: Params<SortedStreamGraphConfig, SortedStreamGraphData>,
        oldParams: Params<SortedStreamGraphConfig, SortedStreamGraphData>,
      ): void {
        if (
          JSON.stringify(newParams.data) !== JSON.stringify(oldParams.data) &&
          this.$refs.component
        ) {
          this.component.update(newParams)
        }
      },
      deep: true,
    },
    filters: {
      async handler(
        newFilters: Record<string, RegularFilterSelectedValues>,
        oldFilters: Record<string, RegularFilterSelectedValues>,
      ): Promise<void> {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          await this.loadData()
        }
      },
      deep: true,
    },
    category: {
      async handler(
        newCategory: CategoriesTreeItem,
        oldCategory: CategoriesTreeItem,
      ): Promise<void> {
        if (JSON.stringify(newCategory) !== JSON.stringify(oldCategory)) {
          await this.loadData()
        }
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new SortedStreamGraphComponent()
  },
  beforeDestroy(): void {
    if (this.hasDataLoaded) {
      this.component.destroy()
    }
  },
  methods: {
    onEnter(): void {
      this.$nextTick(() => {
        if (this.$refs.component) {
          this.component.init(this.$refs.component, this.componentParams)
        }
      })
    },
    async fetchData(cleanData: boolean): Promise<KeywordsDataTop> {
      const { top } =
        cleanData || !this.data
          ? {
              top: {},
            }
          : this.data

      if (!top[this.mode]) {
        this.loadingData = true
        this.isInit = !!this.$refs.component
        top[this.mode] = await this.fetchTopData(this.mode, top)
        this.isInit = true
        this.loadingData = false
      }

      return {
        top,
      }
    },
    async fetchTopData(
      mode: KeywordsCategoryModeEnum,
      top: Dictionary<KeywordData[]> = {},
    ): Promise<KeywordData[]> {
      const id = {
        [KeywordsCategoryModeEnum.GROWTH]: StatsReportsIdsEnum.KEYWORDS_TRENDS,
        [KeywordsCategoryModeEnum.POPULARITY]: StatsReportsIdsEnum.KEYWORDS,
        [KeywordsCategoryModeEnum.CATEGORY]: StatsReportsIdsEnum.KEYWORDS_BY_CATEGORY,
      }[mode]
      const reportResponse = await this.fetchStatsReport({
        id,
        params: {
          ...this.period,
          rows: this.maxKeywordsTop,
          ...(mode === KeywordsCategoryModeEnum.GROWTH && {
            mode: KeywordsTrendsModeEnum.SIGNIFICANCE,
          }),
          ...(this.filters && { filters: this.filters }),
          ...(this.category && { ...this.categoryParams }),
        },
      })
      return Promise.all(
        reportResponse.data.map((overall: KeywordsOverallData) =>
          this.fetchKeywordData(overall, top),
        ),
      )
    },
    async fetchKeywordData(
      overall: KeywordsOverallData,
      top: Dictionary<KeywordData[]>,
    ): Promise<KeywordData> {
      const topKeywordData = this.findKeywordDataInTop(overall.keyword, top)
      const { dist, queries } = topKeywordData || {
        dist: await this.fetchKeywordDistData(overall.keyword),
        queries: undefined,
      }
      return {
        overall,
        dist,
        queries,
      }
    },
    findKeywordDataInTop(keyword: string, top: Dictionary<KeywordData[]>): KeywordData {
      for (const topData of Object.values(top)) {
        const keywordData = topData.find(
          (topKeywordData: KeywordData) => topKeywordData.overall.keyword === keyword,
        )
        if (keywordData) {
          return keywordData
        }
      }
    },
    async fetchKeywordDistData(keyword: string): Promise<DailyKeywordData[]> {
      const reportResponse = await this.fetchStatsReport({
        id: this.isCategoryMode
          ? StatsReportsIdsEnum.DAILY_KEYWORDS_BY_CATEGORY
          : StatsReportsIdsEnum.DAILY_KEYWORDS,
        params: {
          ...this.period,
          ...(this.filters && { filters: this.filters }),
          ...(this.category && { ...this.categoryParams }),
          keyword,
        },
      })
      return reportResponse.data.dist
    },
    transformComponentDataItemValues(keywordData: KeywordData): SortedStreamGraphDataItemValue[] {
      let dataItemValue: SortedStreamGraphDataItemValue
      return keywordData.dist.reduce(
        (
          dataItemValues: SortedStreamGraphDataItemValue[],
          keywordDistDataItem: DailyKeywordData,
        ) => {
          const { name, tooltipName } = this.getComponentDataItemValueNames(
            keywordDistDataItem.date,
          )
          if (!dataItemValue || name !== dataItemValue.name) {
            dataItemValue = {
              name,
              tooltipName,
              value: 0,
            }
            dataItemValues.push(dataItemValue)
          }
          dataItemValue.value += keywordDistDataItem.queryCount
          return dataItemValues
        },
        [],
      )
    },
    getComponentDataItemValueNames(valueDate: Date): {
      name: string
      tooltipName: string
    } {
      const dayString = valueDate.toLocaleDateString(this.$epb.locale, {
        day: '2-digit',
      })
      const weekDayString = valueDate.toLocaleDateString(this.$epb.locale, {
        weekday: 'short',
      })
      const name = `${dayString}<br>${weekDayString}`.toUpperCase()
      const tooltipName = valueDate
        .toLocaleDateString(this.$epb.locale, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
        .toUpperCase()
      return { name, tooltipName }
    },
  },
})
